/*
 * Generated from Empori.Typings.TypescriptTyping
 */

/**
 * Map properties suited for usage within JavaScript.
 */
export interface GoogleMapsJson {

  /**
   * The map height in pixels.
   */
  height: number;

  /**
   * The API key for Google Maps, specified in the Umbraco back-office.
   */
  apiKey: string;

  /**
   * Iframe HTML to replace the JavaScript integration.
   */
  iframeHtml: string;

  /**
   * Initial map zoom.
   */
  initialZoom: number;

  /**
   * Initial map latitude.
   */
  initialLat: number;

  /**
   * Initial map longitude.
   */
  initialLng: number;

  /**
   * A list of map markers.
   */
  markerCollection: GoogleMapsMarkers;
}

/**
 * Used for easier JSON conversion.
 */
export interface GoogleMapsMarkers {

  /**
   * A list of map markers.
   */
  markers: Marker[];
}

/**
 * A single map marker item.
 */
export interface Marker {

  /**
   * The marker's address.
   */
  address: string;

  /**
   * The marker's latitude on the map.
   */
  lat: number;

  /**
   * The marker's longitude on the map.
   */
  lng: number;

  /**
   * The marker's description. Shows upon hoovering on the marker.
   */
  description: string;
}

/**
 * Used for ECMS cookie window content.
 */
export interface CookiesViewModel {

  /**
   * The main header of the cookie window content.
   */
  headerText: string;

  /**
   * The information text.
   */
  text: string;

  /**
   * The displayed label of the accept button.
   */
  acceptButtonText: string;

  /**
   * The displayed label of the read more button.
   */
  readMoreButtonText: string;

  /**
   * The link for the read more button destination.
   */
  readMoreButtonLink: string;
}

/**
 * Hidden text properties suited for usage within JavaScript.
 */
export interface HiddenTextJson {

  /**
   * The cap height of the text in pixels.
   */
  height: number;

  /**
   * Button label when the text is capped.
   */
  showText: string;

  /**
   * Button label when the text is visible.
   */
  hideText: string;
}

/**
 * The model for the entire buttons object. Used now for easier JSON conversion.
 */
export interface ButtonsModel {

  /**
   * The actual buttons.
   */
  buttons: ButtonModel[];
}

/**
 * Standard button properties.
 */
export interface ButtonModel {

  /**
   * The button label.
   */
  text: string;

  /**
   * The link url / href value.
   */
  url: string;

  /**
   * Reference to the FontAwesome icon library.
   */
  iconHtml: string;

  /**
   * If the button's @see Empori.Razor.Controls.ButtonModel.Url should open in a new browser window.
   */
  newWindow: boolean;

  /**
   * Removes the CSS background colors and padding and makes the button look more like plain text.
   */
  displayAsText: boolean;

  /**
   * Uses the alternative color as background color. @see Empori.Razor.Models.Controls.EmporiHeadDesign.SecondaryColor
   */
  inverted: boolean;
}

/**
 * A single weekday item.
 */
export interface OpenHoursDayItem {

  /**
   * What label to show for the day. Usually its the actual day (e.g. Monday).
   */
  dayLabel: string;

  /**
   * When the facility opens this day. Can have any string value.
   * TODO: Try to convert this to a date and use JavaScript for some more useful stuff.
   */
  openHour: string;

  /**
   * When the facility closes this day. Can have any string value.
   * TODO: Try to convert this to a date and use JavaScript for some more useful stuff.
   */
  closeHour: string;
}

/**
 * The model for the list of links.
 */
export interface SocialLinksModel {

  /**
   * The list.
   */
  links: SocialLink[];
}

/**
 * The social link item.
 */
export interface SocialLink {

  /**
   * A pre-defined icon reference.
   */
  linkIcon: LinkIcon;

  /**
   * FontAwesome icon HTML.
   */
  icon: string;

  /**
   * The link label.
   */
  linkLabel: string;

  /**
   * The link source specified by the user.
   */
  linkUrl: string;

  /**
   * The external link to the social media page or platform, forced into https protocol.
   */
  url: string;
}

/**
 * A selection of common social media platforms.
 */
export enum LinkIcon {

  /**
   * Facebook.
   */
  facebook = 0,

  /**
   * Twitter.
   */
  twitter = 1,

  /**
   * Instagram.
   */
  instagram = 2,

  /**
   * LinkedIn.
   */
  linkedIn = 3,

  /**
   * YouTube.
   */
  youtube = 4,

  /**
   * Pinterest.
   */
  pinterest = 5,

  /**
   * Default.
   */
  default = 99
}

/**
 * The model for the entire FAQ-items object. Used now for easier JSON conversion.
 */
export interface FaqModel {

  /**
   * The FAQ items list.
   */
  faqItems: FaqItem[];
}

/**
 * A single FAQ item.
 */
export interface FaqItem {

  /**
   * The question that shows in the block header.
   */
  question: string;

  /**
   * The answer that can be toggled when the @see Empori.Razor.Controls.FaqItem.Question is clicked.
   */
  answer: string;

  /**
   * The icon to show next to the @see Empori.Razor.Controls.FaqItem.Question.
   */
  iconHtml: string;

  /**
   * If the icon should rotate when the answer is toggled.
   */
  enableIconRotate: boolean;
}

/**
 * A scoped @see Empori.Razor.Controls.ResizableImageModel.ResizableImage model.
 */
export interface ResizableImageModel {

  /**
   * An image item.
   */
  resizableImage: ResizableImageItem;
}

/**
 * An image item.
 */
export interface ResizableImageItem {

  /**
   * Image source path.
   */
  imageUrl: string;

  /**
   * The width of the image in pixels.
   */
  width?: number;

  /**
   * The height of the image in pixels.
   */
  height?: number;

  /**
   * If set to true, load this image with the loading="lazy" attribute.
   */
  enableLazyLoading: boolean;

  /**
   * Check if the image @see Empori.Razor.Controls.ResizableImageItem.HyperLink contains http or https.
   */
  isHyperlinkImage: boolean;

  /**
   * An optional link to any source.
   */
  hyperLink: string;

  /**
   * To open the @see Empori.Razor.Controls.ResizableImageItem.HyperLink in a new browser tab.
   */
  openInNewTab: boolean;
}

export interface InputItem {

  /**
   * This identification is used for frontend drag and drop only.
   */
  id: number;

  /**
   * The unique name that is associated with the value.
   */
  uniqueName: string;

  /**
   * Name is used for frontend reference only.
   */
  name: string;
  value: string;
  values: string[];
  label: string;
  type: EmporiInputType;
  placeholder: string;
  description: string;
  required: boolean;

  /**
   * Validation member translates to "pattern" HTML attribute and only works with the following input types: text, date, search, url, tel, email, and password.
   */
  validation: string;

  /**
   * The global title attribute helps describe the pattern to the end user.
   */
  title: string;

  /**
   * Alternatives member is only relevant for select, radio and checkbox input types.
   */
  alternatives: InputAlternative[];

  /**
   * If this member is true, form recipient specified in global and individual settings are ignored.
   * The mailto recipient gets taken from the selected "Alternatives" > "MailTo" value.
   * Only one input in each form can have this member set as true.
   */
  controlsRecipients: boolean;
  icon: string;
}

export enum EmporiInputType {
  text = 0,
  textArea = 1,
  email = 2,
  date = 3,
  select = 4,
  number = 5,
  checkbox = 6,
  radiobutton = 7,
  submit = 8,
  file = 9,
  rte = 10,
  time = 11
}

export interface InputAlternative {
  name: string;
  mailTo: string;
}

/**
 * An image gallery model suited for usage within JavaScript.
 */
export interface ImageGalleryModel {

  /**
   * Properties used for the frontend editor.
   */
  imageGalleryEditorModel: ImageGalleryEditorModel;

  /**
   * The image gallery's delay before showing the next slide if @see Empori.Razor.Controls.ImageGalleryModel.Autoplay is active.
   */
  delay: number;

  /**
   * Whether to autoplay the gallery or not.
   */
  autoplay: boolean;

  /**
   * JavaScript id reference.
   */
  macroId: string;
}

/**
 * The image gallery model for the frontend editor.
 */
export interface ImageGalleryEditorModel {

  /**
   * A list of slides.
   */
  slides: Slide[];

  /**
   * An extra (optional field) to show beneath the slides.
   */
  extraInfo: GalleryExtraInfoModel;
}

/**
 * A slide item.
 */
export interface Slide {

  /**
   * The image source path.
   */
  image: string;

  /**
   * The main title above the @see Empori.Razor.Controls.Slide.Image.
   */
  titleText: string;

  /**
   * The subtitle beneath the @see Empori.Razor.Controls.Slide.TitleText.
   */
  subtitleText: string;

  /**
   * If set to true, it adds an overlay element between the @see Empori.Razor.Controls.Slide.Image and the text elements (@see Empori.Razor.Controls.Slide.TitleText and @see Empori.Razor.Controls.Slide.SubtitleText).
   */
  isImageOverlay: boolean;

  /**
   * The individual slide's buttons.
   */
  buttonsModel: ButtonsModel;
}

/**
 * The model for the slide's extra content suited for usage within JavaScript.
 */
export interface GalleryExtraInfoModel {

  /**
   * Whether to show or hide the extra info.
   */
  isHideExtraInfo: boolean;

  /**
   * The extra info window's columns.
   */
  columns: GalleryExtraInfoColumn[];
}

/**
 * A column item that is used within the @see Empori.Razor.Controls.GalleryExtraInfoModel.
 */
export interface GalleryExtraInfoColumn {

  /**
   * Image source path.
   */
  image: string;

  /**
   * HTML content.
   */
  content: string;
}

/**
 * The counter model for JavaScript usage.
 */
export interface CounterJson {

  /**
   * DOM reference.
   */
  id: string;

  /**
   * Count from this value.
   */
  countFrom: number;

  /**
   * Count to this value.
   */
  countTo: number;

  /**
   * How long the counting should take in milliseconds.
   */
  time: number;

  /**
   * A frontend check whether the counting has started or not.
   */
  started: boolean;
}

/**
 * A single logotype item.
 */
export interface LogotypeItem {

  /**
   * The image source path.
   */
  imageUrl: string;

  /**
   * An optional link to anything. The link is set on the image data type in Umbraco back-office.
   */
  link: string;
}

/**
 * Properties to be used in JavaScript.
 */
export interface FormDetails {

  /**
   * The id of the form.
   */
  formId: string;

  /**
   * The form submit button id.
   */
  formSubmitButtonId: string;

  /**
   * The actual form with its child properties.
   */
  form: Form;

  /**
   * File size cap in MiB (MB).
   */
  globalMaxUploadFileSize?: number;

  /**
   * If the visitor should remain on the same page or not upon form submission.
   */
  stayOnPage: boolean;

  /**
   * DOM reference to the element containing the message HTML.
   */
  messageKey: string;

  /**
   * DOM reference to the element containing the post status messages for "posting form" and "form posted".
   */
  statusKey: string;

  /**
   * The URL to post data to.
   */
  fetchUrl: string;
}

export interface FormPostResponse {
  posts: FormPost[];
}

export interface FormExportQueryString {
  formId: number;
  format?: SpreadsheetFormat;
}

/**
 * Supported spreadsheet formats.
 */
export enum SpreadsheetFormat {

  /**
   * Default XML-based file format for Excel 2010 and Excel 2007.
   */
  xlsx = 0,

  /**
   * Excel 97 - Excel 2003 Binary file format (BIFF8).
   */
  xls = 1,

  /**
   * Comma separated values.
   */
  csv = 2,

  /**
   * JavaScript object notation.
   */
  json = 3
}

export interface OptionsResponse {
  key: string;
  title: string;
  definitions: ObjectDefinition[];
  properties: OptionsProperty[];
  model: { [key: string]: any };
}

export interface ObjectDefinition {
  type: string;
  propertyEditor: string;
  formatString: string;
  properties: OptionsProperty[];
}

export interface OptionsProperty {
  title: string;
  description: string;
  key: string;
  type: string;
  propertyType: OptionsPropertyType;
  tab: string;
  group: string;
  relation: OptionsRelation;
  isAdminOnly?: boolean;
}

/**
 * Options property type of an object definition.
 */
export enum OptionsPropertyType {

  /**
   * Primitive value such as a number or a string.
   */
  primitive = 0,

  /**
   * Nested object structure.
   */
  object = 1,

  /**
   * Array of nested object structures.
   */
  array = 2
}

export interface OptionsRelation {
  multiSelect?: boolean;
  optional?: boolean;
  style: number;
  values: IOptionsValue[];
}

/**
 * Selectable options relation value.
 */
export interface IOptionsValue {

  /**
   * Title of value.
   */
  title: string;

  /**
   * Options value
   */
  value: string;

  /**
   * Description to show to the editor.
   */
  description: string;

  /**
   * Indicate that this value is selected.
   */
  selected?: boolean;
}

/**
 * Type of list style to use for relation values.
 */
export enum RelationListStyle {

  /**
   * A single or a multi select dropdown list.
   */
  dropdown = 0,

  /**
   * A list of radio buttons or a list of checkboxes for multi select.
   */
  buttons = 1
}

export interface MenuOptionsItem {
  title: string;
  key: string;
  icon: string;
  subMenu: MenuOptionsItem[];
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IForm
 */
export interface Form extends EntityBase {

  /**
   * @inheritdoc 
   */
  name: string;
  inputs: InputItem[];
  settings: FormSettings;
}

export interface FormSettings extends BaseOptions {
  submitLabel: string;

  /**
   * Page node to redirect to after form has been posted. See also @see Empori.Core.Middlewares.FormPostMiddleware.
   */
  redirectPageNode: BaseNodePickerOption;

  /**
   * File size in megabytes (MiB).
   */
  maxUploadFileSize?: number;
  allowedFileTypes: AllowedFileType[];
  adminMailOptions: AdminEmailOptions;
}

/**
 * Class contains the bare-minumum fields required for NodePicker property-editor to work.
 * Can be extended with all the fields listed in @see Empori.Core.Configuration.PropertyEditors.BaseNodePickerOption
 */
export interface BaseNodePickerOption {
  nodeId: number;
  name: string;
  icon: string;
}

export interface AllowedFileType {
  type: string;
}

export interface AdminEmailOptions extends MailToOptions {
  sendAdminEmail: GlobalNoYes;
  attachUploadedFiles: GlobalNoYes;
}

/**
 * No/yes override for a global @see System.Boolean option.
 */
export enum GlobalNoYes {

  /**
   * Use global option.
   */
  global = 0,

  /**
   * No.
   */
  no = 1,

  /**
   * Yes.
   */
  yes = 2
}

/**
 * Options for an email message with receiver address, sender address, template and subject.
 */
export interface MailToOptions extends MailOptions {

  /**
   * @inheritdoc 
   */
  toEmail: string;
}

/**
 * Options for an email message with sender address, template and subject.
 */
export interface MailOptions {

  /**
   * @inheritdoc 
   */
  fromEmail: string;

  /**
   * @inheritdoc 
   */
  template: string;

  /**
   * @inheritdoc 
   */
  title: string;
}

/**
 * Base abstract class of a configurable @see Microsoft.Extensions.Options.IOptions`1 in the backoffice.
 */
export interface BaseOptions {
}

export interface EntityBase extends BeingDirtyBase {
  id: number;
  key: string;
  createDate: string;
  updateDate: string;
  deleteDate?: string;
  hasIdentity: boolean;
}

export interface BeingDirtyBase {
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPost
 */
export interface FormPost extends EntityBase {

  /**
   * @inheritdoc 
   */
  formId: number;

  /**
   * @inheritdoc 
   */
  pageId?: number;

  /**
   * @inheritdoc 
   */
  ip: string;

  /**
   * @inheritdoc 
   */
  created: string;
  status: number;

  /**
   * @inheritdoc 
   */
  postData: FormPostData[];

  /**
   * @inheritdoc 
   */
  postFiles: FormPostFile[];
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPostData
 */
export interface FormPostData extends EntityBase {

  /**
   * @inheritdoc 
   */
  formPostId: number;

  /**
   * @inheritdoc 
   */
  name: string;

  /**
   * @inheritdoc 
   */
  value: string;
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPostFile
 */
export interface FormPostFile extends EntityBase {

  /**
   * @inheritdoc 
   */
  formPostId: number;

  /**
   * @inheritdoc 
   */
  fieldName: string;

  /**
   * @inheritdoc 
   */
  fileName: string;

  /**
   * @inheritdoc 
   */
  length: number;

  /**
   * @inheritdoc 
   */
  contentType: string;

  /**
   * @inheritdoc 
   */
  storageName: string;
}

